'use server';

import { createClient } from '@/lib/supabase/server';
import { redirect } from 'next/navigation';
import { headers } from 'next/headers';

async function getOrigin(): Promise<string> {
  // Use NEXT_PUBLIC_URL env var if set (production), otherwise derive from headers
  if (process.env.NEXT_PUBLIC_URL) {
    return process.env.NEXT_PUBLIC_URL;
  }
  
  const headersList = await headers();
  const host = headersList.get('host') || 'localhost:3000';
  const protocol = headersList.get('x-forwarded-proto') || 'https';
  return `${protocol}://${host}`;
}

async function createBusinessForUser(userId: string, businessName: string) {
  try {
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
    const adminApiKey = process.env.ADMIN_API_KEY;
    
    if (!adminApiKey) {
      console.error('ADMIN_API_KEY not configured - cannot create business');
      return;
    }
    
    const response = await fetch(`${backendUrl}/api/create-business`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Api-Key': adminApiKey,
      },
      body: JSON.stringify({
        user_id: userId,
        business_name: businessName,
      }),
    });

    if (response.ok) {
      const result = await response.json();
      console.log(`Business "${businessName}" created for user ${userId}:`, result);
    } else {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Failed to create business for user ${userId}:`, errorData);
    }
  } catch (error) {
    console.error('Error creating business:', error);
  }
}

async function sendWelcomeEmail(email: string, name?: string) {
  try {
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
    const adminApiKey = process.env.ADMIN_API_KEY;
    
    if (!adminApiKey) {
      console.error('ADMIN_API_KEY not configured');
      return;
    }
    
    const response = await fetch(`${backendUrl}/api/send-welcome-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Api-Key': adminApiKey,
      },
      body: JSON.stringify({
        email,
        name,
      }),
    });

    if (response.ok) {
      console.log(`Welcome email queued for ${email}`);
    } else {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Failed to queue welcome email for ${email}:`, errorData);
    }
  } catch (error) {
    console.error('Error sending welcome email:', error);
  }
}

export async function signIn(prevState: any, formData: FormData) {
  const email = formData.get('email') as string;
  const password = formData.get('password') as string;
  const returnUrl = formData.get('returnUrl') as string | undefined;

  if (!email || !email.includes('@')) {
    return { message: 'Please enter a valid email address' };
  }

  if (!password || password.length < 6) {
    return { message: 'Password must be at least 6 characters' };
  }

  const supabase = await createClient();

  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    return { message: error.message || 'Could not authenticate user' };
  }

  // Use client-side navigation instead of server-side redirect
  return { success: true, redirectTo: returnUrl || '/dashboard' };
}

export async function signUp(prevState: any, formData: FormData) {
  const email = formData.get('email') as string;
  const password = formData.get('password') as string;
  const confirmPassword = formData.get('confirmPassword') as string;
  const businessName = formData.get('businessName') as string;
  const invitationToken = formData.get('invitationToken') as string | null;
  const returnUrl = formData.get('returnUrl') as string | undefined;
  const termsAccepted = formData.get('termsAccepted') as string;
  
  const isInvitedSignup = !!invitationToken;
  
  // Get origin from server-side (more reliable than client-passed value)
  const origin = await getOrigin();

  if (!email || !email.includes('@')) {
    return { message: 'Please enter a valid email address' };
  }

  if (!password || password.length < 6) {
    return { message: 'Password must be at least 6 characters' };
  }

  if (password !== confirmPassword) {
    return { message: 'Passwords do not match' };
  }

  // Business name only required for non-invited signups
  if (!isInvitedSignup && (!businessName || businessName.trim().length < 2)) {
    return { message: 'Please enter a valid business name' };
  }

  if (termsAccepted !== 'true') {
    return { message: 'You must agree to the Terms & Conditions to create an account' };
  }

  const supabase = await createClient();

  // Build redirect URL — for invited signups, redirect back to invitation page
  let callbackUrl = `${origin}/auth/callback`;
  const callbackParams = new URLSearchParams();
  if (isInvitedSignup) {
    callbackParams.set('returnUrl', `/invitation?token=${invitationToken}`);
    callbackParams.set('invitation_token', invitationToken);
  } else if (returnUrl) {
    callbackParams.set('returnUrl', returnUrl);
  }
  if (callbackParams.toString()) {
    callbackUrl += `?${callbackParams.toString()}`;
  }

  // Build user metadata
  const metadata: Record<string, string> = {};
  if (isInvitedSignup) {
    metadata.invitation_token = invitationToken;
  } else {
    metadata.business_name = businessName.trim();
  }

  const { error, data } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: callbackUrl,
      data: metadata,
    },
  });

  if (error) {
    return { message: error.message || 'Could not create account' };
  }

  // We now defer sign-in and business creation until AFTER email confirmation.
  // This prevents unverified accounts getting businesses prematurely.
  return {
    success: true,
    message: 'Account created! Check your email to confirm your registration.',
  };
}

export async function forgotPassword(prevState: any, formData: FormData) {
  const email = formData.get('email') as string;

  if (!email || !email.includes('@')) {
    return { message: 'Please enter a valid email address' };
  }

  // Get origin from server-side (more reliable than client-passed value)
  const origin = await getOrigin();

  const supabase = await createClient();

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${origin}/auth/callback?next=/auth/reset-password`,
  });

  if (error) {
    return { message: error.message || 'Could not send password reset email' };
  }

  return {
    success: true,
    message: 'Check your email for a password reset link',
  };
}

export async function resetPassword(prevState: any, formData: FormData) {
  const password = formData.get('password') as string;
  const confirmPassword = formData.get('confirmPassword') as string;

  if (!password || password.length < 6) {
    return { message: 'Password must be at least 6 characters' };
  }

  if (password !== confirmPassword) {
    return { message: 'Passwords do not match' };
  }

  const supabase = await createClient();

  const { error } = await supabase.auth.updateUser({
    password,
  });

  if (error) {
    return { message: error.message || 'Could not update password' };
  }

  return {
    success: true,
    message: 'Password updated successfully',
  };
}

export async function signInWithMagicLink(prevState: any, formData: FormData) {
  const email = formData.get('email') as string;
  const returnUrl = formData.get('returnUrl') as string | undefined;

  if (!email || !email.includes('@')) {
    return { message: 'Please enter a valid email address' };
  }

  const origin = await getOrigin();
  const supabase = await createClient();

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${origin}/auth/confirm?next=${returnUrl || '/dashboard'}`,
      shouldCreateUser: false, // Only allow existing users to use magic link
    },
  });

  if (error) {
    return { message: error.message || 'Could not send magic link' };
  }

  return {
    success: true,
    message: 'Check your email for a magic login link',
  };
}

export async function completeBusinessSetup(prevState: any, formData: FormData) {
  const businessName = formData.get('businessName') as string;
  const termsAccepted = formData.get('termsAccepted') as string;
  const returnUrl = formData.get('returnUrl') as string | undefined;

  if (!businessName || businessName.trim().length < 2) {
    return { message: 'Please enter a valid business name' };
  }

  if (termsAccepted !== 'true') {
    return { message: 'You must agree to the Terms & Conditions to continue' };
  }

  const supabase = await createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    return { message: 'Your session has expired. Please sign in again.' };
  }

  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
  const adminApiKey = process.env.ADMIN_API_KEY;

  if (!backendUrl || !adminApiKey) {
    console.error(
      'Missing NEXT_PUBLIC_BACKEND_URL or ADMIN_API_KEY - cannot create business',
    );
    return { message: 'Server configuration error. Please contact support.' };
  }

  try {
    const response = await fetch(`${backendUrl}/api/create-business`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Api-Key': adminApiKey,
      },
      body: JSON.stringify({
        user_id: user.id,
        business_name: businessName.trim(),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      console.error(
        'create-business failed during business setup:',
        response.status,
        errorText,
      );
      return { message: 'Could not create your business. Please try again.' };
    }
  } catch (error) {
    console.error('Error creating business during setup:', error);
    return { message: 'Could not create your business. Please try again.' };
  }

  return { success: true, redirectTo: returnUrl || '/dashboard' };
}

export async function signOut() {
  const supabase = await createClient();
  const { error } = await supabase.auth.signOut();

  if (error) {
    return { message: error.message || 'Could not sign out' };
  }

  return redirect('/');
}
